import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { merge, Observable, map, reduce, of, switchMap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LocationRouteCffService } from '@fcom/core';
import { LocationType } from '@fcom/core-api';
import { BoundType, LocationInfoMap } from '@fcom/dapi';
import { FinnairBoundItem, FinnairLocation, FinnairLocationType } from '@fcom/dapi/api/models';
import { LanguageService } from '@fcom/ui-translate';
import { getKeyCodeFromEvent, KeyCodes, LocalDate, unique } from '@fcom/core/utils';
import { BUS_AIRCRAFT_CODE } from '@fcom/common-booking/interfaces';
import { isFlight } from '@fcom/common-booking/utils/flight.utils';

import { TimetableLocationsService } from '../../services/timetable-locations.service';
import { TimetableScheduleItem } from '../../interfaces';

@Component({
  selector: 'fin-timetable-schedule',
  templateUrl: './timetable-schedule.component.html',
  styleUrls: ['timetable-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimetableScheduleComponent {
  private locationRouteCffService = inject(LocationRouteCffService);
  private languageService = inject(LanguageService);
  private timetableLocations = inject(TimetableLocationsService);

  /** A list of bounds used to compile the schedule table. */
  readonly bounds = input.required<FinnairBoundItem[]>();
  readonly loading = input.required<boolean>();
  readonly originCode = input<string | undefined>(undefined);
  readonly destinationCode = input<string | undefined>(undefined);

  /** Defaults to the existing timetable-page key */
  readonly selectDateKey = input<string>('timetable.flightList.selectDate');

  public readonly BoundType = BoundType;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  readonly items = computed<TimetableScheduleItem[]>(() =>
    this.bounds().map((bound) => ({
      flights: bound.itinerary.filter(isFlight).map((flight) => ({
        flightNumber: flight.flightNumber ?? '',
        departureTime: this.getTime(flight.departure.dateTime),
        arrivalTime: this.getTime(flight.arrival.dateTime),
        overnight: this.isOvernight(flight.departure.dateTime, flight.arrival.dateTime),
        icon: flight.aircraft?.code === BUS_AIRCRAFT_CODE ? SvgLibraryIcon.BUS : SvgLibraryIcon.AIRCRAFT_HORIZONTAL,
      })),
      duration: bound.duration,
      origin$: this.timetableLocations.getCityNameForLocationCode(bound.departure.locationCode),
      destination$: this.timetableLocations.getCityNameForLocationCode(bound.arrival.locationCode),
      departureTime: this.getTime(bound.departure.dateTime),
      arrivalTime: this.getTime(bound.arrival.dateTime),
    }))
  );

  readonly selectedBound = signal<FinnairBoundItem | undefined>(undefined);
  readonly locations = toSignal(
    toObservable(this.selectedBound).pipe(
      switchMap((bound) => (bound ? this.getLocations(this.locationCodesFor(bound)) : of({} as LocationInfoMap)))
    ),
    { initialValue: {} as LocationInfoMap }
  );
  detailsModalOpen = false;

  openModal(event: MouseEvent | KeyboardEvent, item: TimetableScheduleItem): void {
    event.stopPropagation();

    this.selectedBound.set(
      this.bounds().find(
        (bound) =>
          bound.itinerary
            .filter(isFlight)
            .map((flight) => flight.flightNumber)
            .join(',') === item.flights.map((flight) => flight.flightNumber).join(',')
      )
    );
    this.detailsModalOpen = true;
  }

  closeModal(): void {
    this.detailsModalOpen = false;
  }

  rowKeyPress(keyboardEvent: KeyboardEvent, item: TimetableScheduleItem): void {
    if (getKeyCodeFromEvent(keyboardEvent).keyCode !== KeyCodes.ENTER.keyCode) {
      return;
    }

    this.openModal(keyboardEvent, item);
  }

  private locationCodesFor(bound: FinnairBoundItem): string[] {
    return bound.itinerary
      .filter(isFlight)
      .reduce(
        (locationCodes, flight) => locationCodes.concat([flight.departure.locationCode, flight.arrival.locationCode]),
        [] as string[]
      )
      .filter(unique);
  }

  private getLocations(locationCodes: string[]): Observable<LocationInfoMap> {
    const locations: Array<Observable<FinnairLocation>> = locationCodes.map((locationCode) =>
      this.getLocation(locationCode)
    );
    return merge(...locations).pipe(
      reduce((acc, location) => {
        acc[location.cityCode] = location;
        return acc;
      }, {} as LocationInfoMap)
    );
  }

  private getLocation(locationCode: string): Observable<FinnairLocation> {
    return this.locationRouteCffService.bestGuessFor(locationCode, this.languageService.localeValue).pipe(
      map((location) => {
        /**
         * TODO: remove the casting when backend FinnairPicture is fixed
         */
        return {
          ...location,
          type: location.type === LocationType.AIRPORT ? FinnairLocationType.AIRPORT : FinnairLocationType.CITY,
          cityCode: locationCode,
          name: location.title,
        } as unknown as FinnairLocation;
      })
    );
  }

  private isOvernight(departureTime: string, arrivalTime: string): boolean {
    const departureDate: LocalDate = new LocalDate(this.getDate(departureTime));
    const arrivalDate: LocalDate = new LocalDate(this.getDate(arrivalTime));
    const nextDay: LocalDate = departureDate.plusDays(1);
    return (
      arrivalDate.localDay === nextDay.localDay &&
      arrivalDate.localMonth === nextDay.localMonth &&
      arrivalDate.localYear === nextDay.localYear
    );
  }

  private getDate(dateTime: string): string {
    return dateTime.substring(0, dateTime.indexOf('T'));
  }

  private getTime(dateTime: string): string {
    return dateTime.substr(dateTime.indexOf('T') + 1, 5);
  }
}
